import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { User } from '../../../../backend/src/users/user.entity';
import { Role } from '../../../../backend/src/users/role.entity';
import { NotificationService } from '../notification/notification.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { AuthService } from '../auth/auth.service';
import { initialsOf, roleColor } from './user-ui';

interface RoleChipVm {
  label: string;
  color: string;
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit {

  users: User[] = [];
  roles: Role[] = [];
  fetching = false;
  search = '';

  editorOpen = false;
  selectedUser: User | null = null;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private confirmDialogService: ConfirmDialogService,
    private authService: AuthService,
  ) { }

  /** A sorban szereplő felhasználó a jelenleg bejelentkezett felhasználó-e. */
  isSelf(user: User): boolean {
    return user.id != null && user.id === this.authService.user?.id;
  }

  ngOnInit(): void {
    this.fetchUsers();
    this.fetchRoles();
  }

  fetchUsers() {
    this.fetching = true;
    this.http.get<User[]>('users').subscribe(users => {
      this.users = users || [];
      this.fetching = false;
    }, e => {
      this.notificationService.show(`Hiba a felhasználók betöltése során! Részletek: ${e.message}`, 'danger');
      this.fetching = false;
    });
  }

  fetchRoles() {
    this.http.get<Role[]>('users/roles').subscribe(roles => {
      this.roles = roles || [];
    }, () => {
      // A szerkesztő szerep nélkül is megnyitható; a mentésnél a lista üres marad.
    });
  }

  /** Keresés név vagy e-mail (és felhasználónév) szerint — prototípus szűrés. */
  get filteredUsers(): User[] {
    const q = this.search.trim().toLowerCase();
    if (!q) {
      return this.users;
    }
    return this.users.filter(u =>
      (u.full_name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.username || '').toLowerCase().includes(q));
  }

  trackById(_index: number, user: User) {
    return user.id;
  }

  initials(user: User): string {
    return initialsOf(user.full_name || user.username);
  }

  /** Avatar háttér: az első szerep színe, különben #9AA098 (prototípus). */
  avatarColor(user: User): string {
    const first = user.roles && user.roles.length ? user.roles[0] : null;
    const label = first?.role?.description || first?.role?.name;
    return roleColor(label) || '#9AA098';
  }

  roleChips(user: User): RoleChipVm[] {
    return (user.roles || [])
      .map(ur => {
        const label = ur?.role?.description || ur?.role?.name || '';
        return {
          label,
          color: roleColor(label) || '#707870',
        };
      })
      .filter(chip => !!chip.label);
  }

  isActive(user: User): boolean {
    return user.enabled === 1;
  }

  /** Külsős munkatárs — a listában halvány jelölést kap (alkalmazott a normál eset). */
  isExternal(user: User): boolean {
    return user.employment_type === 'external';
  }

  addUser() {
    this.selectedUser = null;
    this.editorOpen = true;
  }

  editUser(user: User) {
    this.selectedUser = user;
    this.editorOpen = true;
  }

  closeEditor() {
    this.editorOpen = false;
    this.selectedUser = null;
  }

  onSaved() {
    this.closeEditor();
    this.fetchUsers();
  }

  removeUser(user: User) {
    // A saját fiók nem törölhető (a backend is tiltja) — itt is meggátoljuk,
    // hogy egyértelmű visszajelzést adjunk kattintás helyett.
    if (this.isSelf(user)) {
      this.notificationService.show('A saját fiókodat nem törölheted.', 'danger');
      return;
    }
    const name = user.full_name || user.username || user.email;
    this.confirmDialogService.open({
      title: 'Biztosan törlöd?',
      message: `A(z) „${name}” felhasználó törlődik.`,
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
      tone: 'danger',
    }).subscribe(result => {
      if (!result?.confirmed) {
        return;
      }
      this.http.delete<{ result?: string; reason?: string }>(`users/${user.id}`).subscribe(res => {
        // A backend hivatkozó adatok esetén csak letiltja a felhasználót
        // (soft delete) — ilyenkor ezt jelezzük, nem „törölve"-t.
        if (res?.result === 'disabled') {
          this.notificationService.show(res.reason || 'A felhasználó letiltva (kapcsolódó adatok miatt nem törölhető).', 'success');
        } else {
          this.notificationService.show('Felhasználó törölve!', 'success');
        }
        this.fetchUsers();
      }, e => {
        this.notificationService.show(`Hiba a felhasználó törlése során! Részletek: ${e.error?.message || e.message}`, 'danger');
      });
    });
  }
}
